"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { motion, useReducedMotion, useInView, animate } from "framer-motion";
import { Check, X } from "lucide-react";
import { CalendlyPopupButton } from "@/components/CalendlyPopupButton";
import Link from "@/components/LocalizedLink";
import { Button, CtaButtonIcon, ctaButtonClasses } from "@/components/ui/cta-button";
import { BADGE_HREF_MAP } from "@/components/solutions/FeatureSection";
import { DynamicIcon } from "@/components/DynamicIcon";
import { FaqSection } from "@/components/home/frequently-asked";
import { ServiceCtaBand } from "@/components/services/ServiceCtaBand";
import ProcessSection from "@/components/process-section";
import TelemedicineJourney, {
  type HealthTechJourney,
} from "@/components/health-tech/TelemedicineJourney";
import IntegrationEcosystemDiagram, {
  type EcosystemData,
} from "@/components/health-tech/IntegrationEcosystemDiagram";
import {
  renderFormattedText,
  EXTERNAL_STANDARDS_LINKS,
} from "@/components/health-tech/formatHealthTechText";

export interface HealthTechFeature {
  icon: string;
  title: string;
  description: string;
  iconStyle?: "box" | "3d";
  tags?: string[];
  learnMoreHref?: string;
}

export interface HealthTechSection {
  title: string;
  boldTitle?: string;
  description: string;
  features: HealthTechFeature[];
  badges?: string[];
  compliance?: { name: string; status: string }[];
  techStack?: string[];
}

export interface HealthTechShowcase {
  title: string;
  boldTitle?: string;
  description: string;
  image: { src: string; alt: string; width: number; height: number };
  caption?: string;
  points?: string[];
}

export interface HealthTechProcessStep {
  number: string;
  title: string;
  description: string;
}

export interface HealthTechFaq {
  question: string;
  answer: string;
}

export interface HealthTechComparisonRow {
  label: string;
  offTheShelf: string;
  custom: string;
}

export interface HealthTechComparison {
  eyebrow?: string;
  title: string;
  boldTitle?: string;
  description: string;
  columns: {
    label: string;
    subtext?: string;
  }[];
  rows: HealthTechComparisonRow[];
  closingLine?: string;
  ctaText?: string;
  ctaHref?: string;
}

export interface HealthTechStat {
  value: string;
  label: string;
  /** Internal route to the evidence behind the stat (e.g. the ISO certificate in the Trust Center). */
  href?: string;
}

export interface HealthTechTopic {
  slug: string;
  hubTitle: string;
  hubDescription: string;
  hubIcon: string;

  metadata: {
    title: string;
    description: string;
    canonical: string;
    keywords: string[];
    ogTitle: string;
    ogDescription: string;
  };

  hero: {
    breadcrumbs: { label: string; href: string }[];
    title: string;
    boldTitle: string;
    description: string;
    buttonText: string;
    buttonHref: string;
    calendlyText?: string;
    calendlyUrl?: string;
    stats: HealthTechStat[];
  };

  overview: HealthTechSection;
  features: HealthTechSection;
  whyChoose: HealthTechSection;

  /** Optional interactive phone walkthrough (currently telemedicine only). */
  journey?: HealthTechJourney;

  /** Optional "custom vs. off-the-shelf" comparison table (currently dental only). */
  comparison?: HealthTechComparison;

  /** Optional "what we integrate" ecosystem diagram (currently FHIR Interoperability only). */
  ecosystem?: EcosystemData;

  /**
   * Optional per-specialty breakdown of the offering (currently intake forms
   * only). Rendered as stacked rows so each specialty gets room for the
   * discipline-specific detail that makes it distinct from the others.
   */
  specialties?: HealthTechSection;
  /** Render every specialty row expanded (grouped specialty pages). Default: accordion. */
  specialtiesAllOpen?: boolean;

  /** Optional starting-template library (currently intake forms only). */
  templates?: HealthTechSection;

  /**
   * Optional product screenshot with supporting copy (currently intake forms
   * only). Use it where a claim is easier to show than to describe — the
   * intake page pairs it with the conditional-logic builder.
   */
  showcase?: HealthTechShowcase;

  process: {
    title: string;
    boldTitle: string;
    description: string;
    steps: HealthTechProcessStep[];
  };

  faq: HealthTechFaq[];

  /** Optional outbound citations to authoritative standards bodies (e.g. HL7, ONC, CMS). */
  sources?: { label: string; url: string }[];
}

interface HealthTechTemplateProps {
  topic: HealthTechTopic;
  /** Server-rendered related-insights block, shown just above the CTA band. */
  related?: React.ReactNode;
}

const EASE = [0.16, 1, 0.3, 1] as const;

/**
 * Masked line reveal — same recipe as the homepage/service heroes.
 * CSS-driven (see `.hero-line-rise` in globals.css) so the h1 — the LCP
 * element on these pages — paints in its final position at first render
 * instead of waiting on framer-motion hydration.
 */
const RevealLine = ({
  delay,
  children,
}: {
  delay: number;
  children: React.ReactNode;
}) => {
  return (
    <span className="block overflow-hidden">
      <span className="hero-line-rise block" style={{ animationDelay: `${delay}s` }}>
        {children}
      </span>
    </span>
  );
};

/** Count-up numeral: parses "24/7", "60%", "3x" into number + suffix. */
const StatValue = ({ value }: { value: string }) => {
  const prefersReduced = useReducedMotion();
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "0px" });

  const match = value.match(/^(\d+)(.*)$/);
  const target = match ? parseInt(match[1], 10) : null;
  const suffix = match ? match[2] : "";

  const [display, setDisplay] = useState<number | null>(target);

  useEffect(() => {
    if (target !== null && !prefersReduced && !inView) {
      setDisplay(0);
    }
  }, [inView, target, prefersReduced]);

  useEffect(() => {
    if (target === null || prefersReduced || !inView) return;
    const controls = animate(0, target, {
      duration: 1.4,
      ease: EASE,
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, target, prefersReduced]);

  // Fallback: if the intersection observer never fires, ensure the final value is shown after a delay.
  useEffect(() => {
    if (target === null || prefersReduced) return;
    const id = setTimeout(() => setDisplay(target), 2000);
    return () => clearTimeout(id);
  }, [target, prefersReduced]);

  if (target === null) return <>{value}</>;
  return (
    <span ref={ref} className="tabular-nums">
      {display}
      {suffix}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Hero — short dark scene (skeleton 1), same recipe as ServiceHero, plus a
// stat strip under a hairline border (the topic's own trust/meta strip).
// ---------------------------------------------------------------------------
function Hero({
  data,
  hubIcon,
}: {
  data: HealthTechTopic["hero"];
  hubIcon: string;
}) {
  const prefersReduced = useReducedMotion();

  const fadeUp = (delay: number) =>
    prefersReduced
      ? {}
      : {
          initial: { opacity: 0, y: 16 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.6, ease: EASE, delay },
        };

  return (
    <section className="relative flex min-h-svh w-full flex-col justify-end overflow-hidden text-foreground">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "var(--scene-hero)",
        }}
      >
        <div className="texture-slats absolute inset-y-0 left-0 w-[55%] opacity-80" />
        <div className="texture-grain absolute inset-0 opacity-40" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[88rem] px-5 pt-32 sm:px-8 lg:px-12">
        {data.breadcrumbs && data.breadcrumbs.length > 0 && (
          <p className="mb-6 flex flex-wrap items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-foreground/70">
            {data.breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.href + crumb.label}>
                <Link href={crumb.href} className="transition-colors hover:text-orange-500">
                  {crumb.label}
                </Link>
                {index < data.breadcrumbs.length - 1 && (
                  <span aria-hidden="true" className="text-foreground/40">
                    /
                  </span>
                )}
              </React.Fragment>
            ))}
          </p>
        )}

        <h1
          className="font-display max-w-4xl text-balance font-semibold leading-[1.06] tracking-[-0.02em] text-foreground"
          style={{ fontSize: "clamp(2rem, 4.6vw, 3.6rem)" }}
        >
          <RevealLine delay={0.18}>
            {data.title} <span className="text-foreground/60">{data.boldTitle}</span>
          </RevealLine>
        </h1>

        <p
          className="fade-up-rise mt-6 max-w-xl text-lg leading-relaxed text-foreground/80"
          style={{ animationDelay: "0.35s" }}
        >
          {renderFormattedText(data.description)}
        </p>

        <div className="mt-9 flex flex-wrap items-center gap-3">
          <Button href={data.buttonHref} variant="primary">
            {data.buttonText}
          </Button>
          {data.calendlyText && data.calendlyUrl && (
            <CalendlyPopupButton
              url={data.calendlyUrl}
              text={
                <>
                  {data.calendlyText}
                  <CtaButtonIcon />
                </>
              }
              className={ctaButtonClasses({ variant: "secondary" })}
            />
          )}
        </div>

        {data.stats && data.stats.length > 0 && (
          <div className="mt-14 grid grid-cols-2 divide-x divide-y divide-foreground/10 border-y border-foreground/10 sm:grid-cols-4 sm:divide-y-0 md:mt-16">
            {data.stats.map((stat) => (
              <div key={stat.label} className="px-4 py-6 first:pl-0 sm:px-6">
                <div
                  className="font-display font-semibold tracking-[-0.02em] text-foreground"
                  style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)" }}
                >
                  {stat.href ? (
                    <Link href={stat.href} className="transition-colors hover:text-orange-500">
                      <span className="tabular-nums">{stat.value}</span>
                    </Link>
                  ) : stat.value.trim() === "R4 / R5" ? (
                    <a
                      href="https://hl7.org/fhir/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="transition-colors hover:text-orange-500"
                    >
                      <span className="tabular-nums">{stat.value}</span>
                    </a>
                  ) : stat.value.trim().toLowerCase() === "hl7 v2" ? (
                    <a
                      href="https://www.hl7.org/implement/standards/product_brief.cfm?product_id=185"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="transition-colors hover:text-orange-500"
                    >
                      <span className="tabular-nums">{stat.value}</span>
                    </a>
                  ) : (
                    <span className="tabular-nums">{stat.value}</span>
                  )}
                </div>
                <div className="mt-1 text-sm text-foreground/70">{renderFormattedText(stat.label)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Benefits rows (skeleton 5) — used for both `overview` and `whyChoose`,
// which share the same shape: a short list of icon + title + description.
// ---------------------------------------------------------------------------
function BenefitsRows({ data }: { data: HealthTechSection }) {
  const prefersReduced = useReducedMotion();

  if (!data.features || data.features.length === 0) return null;

  return (
    <section className="relative z-10 bg-transparent px-5 py-24 text-foreground sm:px-8 md:py-36 lg:px-12">
      <div className="mx-auto max-w-[88rem]">
        <div className="max-w-2xl">
          <h2 className="font-display text-balance text-3xl font-semibold leading-[1.08] tracking-[-0.02em] md:text-4xl">
            {data.title} {data.boldTitle}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-foreground/80 md:text-lg">
            {renderFormattedText(data.description)}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-x-16 gap-y-10 md:grid-cols-3">
          {data.features.map((feature, index) => (
            <motion.div
              key={feature.title}
              className="border-t border-border pt-8"
              initial={prefersReduced ? false : { opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.6, ease: EASE, delay: (index % 3) * 0.1 }}
            >
              <div className="flex items-center gap-3">
                <DynamicIcon
                  name={feature.icon}
                  size={20}
                  strokeWidth={1.75}
                  className="flex-shrink-0 text-orange-700 dark:text-orange-400"
                  aria-hidden="true"
                />
                <h3 className="font-display text-xl font-semibold tracking-[-0.01em] text-foreground">
                  {feature.title}
                </h3>
              </div>
              <p className="mt-3 max-w-lg text-base leading-relaxed text-foreground/80">
                {renderFormattedText(feature.description)}
              </p>
              {feature.learnMoreHref && (
                <Link
                  href={feature.learnMoreHref}
                  className="group mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-foreground transition-colors hover:text-orange-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                >
                  Learn more
                  <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">
                    →
                  </span>
                </Link>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Capability rows (skeleton 3) — `features`, which is richer (has tags,
// badges, tech stack) so it earns the accordion treatment.
// ---------------------------------------------------------------------------
function CapabilitySection({ data, allOpen = false }: { data: HealthTechSection; allOpen?: boolean }) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);
  // allOpen: every row expanded and static, so all of the text is in the
  // server-rendered HTML (crawlers that don't click accordions still read it).
  const isOpen = (index: number) => allOpen || index === expandedIndex;

  if (!data.features || data.features.length === 0) return null;

  return (
    <section className="relative z-10 bg-transparent px-5 py-24 text-foreground sm:px-8 md:py-36 lg:px-12">
      <div className="mx-auto grid max-w-[88rem] gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <h2 className="font-display text-balance text-3xl font-semibold leading-[1.08] tracking-[-0.02em] md:text-4xl">
              {data.title} {data.boldTitle}
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-foreground/80 md:text-lg">
              {renderFormattedText(data.description)}
            </p>

            {data.badges && data.badges.length > 0 && (
              <div className="mt-8 flex flex-wrap gap-2">
                {data.badges.map((badge, idx) => {
                  const name = typeof badge === "string" ? badge : (badge as any).name;
                  const href =
                    typeof badge === "string"
                      ? BADGE_HREF_MAP[name]
                      : (badge as any).href || BADGE_HREF_MAP[name];

                  if (href) {
                    return (
                      <Link
                        key={name || idx}
                        href={href}
                        className="inline-flex items-center rounded-full border border-border px-3.5 py-1.5 text-xs font-medium text-foreground/70 transition-all hover:border-orange-500 hover:bg-orange-500/10 hover:text-orange-500 cursor-pointer"
                      >
                        {name}
                      </Link>
                    );
                  }

                  return (
                    <span
                      key={name || idx}
                      className="rounded-full border border-border px-3.5 py-1.5 text-xs font-medium text-foreground/70"
                    >
                      {name}
                    </span>
                  );
                })}
              </div>
            )}

            {data.techStack && data.techStack.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {data.techStack.map((tech) => {
                  const externalUrl = EXTERNAL_STANDARDS_LINKS[tech];
                  if (externalUrl) {
                    return (
                      <a
                        key={tech}
                        href={externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 rounded-full border border-orange-500/30 bg-orange-500/5 px-3.5 py-1.5 text-xs font-medium text-orange-700 dark:text-orange-400 transition-all hover:border-orange-500 hover:bg-orange-500/15 hover:text-orange-600"
                      >
                        <span>{tech}</span>
                        <span aria-hidden="true" className="text-[10px] opacity-70">↗</span>
                      </a>
                    );
                  }
                  return (
                    <span
                      key={tech}
                      className="rounded-full border border-orange-500/20 bg-orange-500/5 px-3.5 py-1.5 text-xs font-medium text-orange-700 dark:text-orange-400"
                    >
                      {tech}
                    </span>
                  );
                })}
              </div>
            )}

            {data.compliance && data.compliance.length > 0 && (
              <div className="mt-8 divide-y divide-border border-y border-border">
                {data.compliance.map((item) => (
                  <div
                    key={item.name}
                    className="flex items-center justify-between gap-4 py-3"
                  >
                    <span className="text-sm text-foreground/80">{item.name}</span>
                    <span className="flex flex-shrink-0 items-center gap-1.5 text-xs font-medium text-orange-700 dark:text-orange-400">
                      <Check size={13} strokeWidth={2.5} aria-hidden="true" />
                      {item.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-7">
          {data.features.map((feature, index) => (
            <div key={feature.title} className="border-t border-border last:border-b">
              {allOpen ? (
                <h3 className="py-6 font-display text-xl font-semibold tracking-[-0.01em] text-foreground md:py-7 md:text-2xl">
                  {feature.title}
                </h3>
              ) : (
              <h3>
                <button
                  type="button"
                  onClick={() =>
                    setExpandedIndex((prev) => (prev === index ? null : index))
                  }
                  aria-expanded={index === expandedIndex}
                  className="group flex w-full cursor-pointer items-center justify-between gap-6 py-6 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 md:py-7"
                >
                  <span
                    className={`font-display text-xl font-semibold tracking-[-0.01em] transition-colors duration-300 md:text-2xl ${
                      index === expandedIndex
                        ? "text-orange-700 dark:text-orange-400"
                        : "text-foreground group-hover:text-orange-500"
                    }`}
                  >
                    {feature.title}
                  </span>
                  <span
                    aria-hidden="true"
                    className={`flex-shrink-0 text-2xl font-light transition-transform duration-300 ${
                      index === expandedIndex ? "rotate-45 text-orange-700 dark:text-orange-400" : "text-foreground/70"
                    }`}
                  >
                    +
                  </span>
                </button>
              </h3>
              )}
              {isOpen(index) && (
                <div className={`overflow-hidden pb-7${allOpen ? " -mt-2" : ""}`}>
                  <p className="max-w-xl text-base leading-relaxed text-foreground/80 md:text-lg">
                    {renderFormattedText(feature.description)}
                  </p>
                  {feature.tags && feature.tags.length > 0 && (
                    <p className="mt-4 text-sm text-foreground/60">
                      {feature.tags.join("  ·  ")}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Comparison table (skeleton 6) — "custom-built vs. off-the-shelf" style
// section. Currently used on the dental practice management topic only;
// renders nothing when the topic has no `comparison` data.
// ---------------------------------------------------------------------------
/** Mobile-only stacked card — one criterion, its two answers, a hairline between them. */
function ComparisonRowMobile({
  row,
  index,
}: {
  row: HealthTechComparisonRow;
  index: number;
}) {
  const prefersReduced = useReducedMotion();

  return (
    <motion.div
      role="group"
      className="border-t border-border py-8 last:border-b"
      initial={prefersReduced ? false : { opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5, ease: EASE, delay: (index % 5) * 0.06 }}
    >
      <h3 className="font-display text-lg font-semibold leading-snug tracking-[-0.01em] text-foreground">
        {row.label}
      </h3>

      <div className="mt-5 divide-y divide-border/60">
        <div className="pb-5">
          <p className="text-xs font-medium uppercase tracking-[0.16em] text-foreground/65">
            Off-the-Shelf
          </p>
          <p className="mt-2 flex items-start gap-2.5 text-sm leading-relaxed text-foreground/60">
            <X
              aria-hidden="true"
              size={15}
              strokeWidth={2.5}
              className="mt-[0.15em] flex-shrink-0 text-destructive"
            />
            <span>{renderFormattedText(row.offTheShelf)}</span>
          </p>
        </div>
        <div className="pt-5">
          <p className="text-xs font-medium uppercase tracking-[0.16em] text-foreground/65">
            Custom-Built with Woltrio
          </p>
          <p className="mt-2 flex items-start gap-2.5 text-sm leading-relaxed text-foreground">
            <Check
              aria-hidden="true"
              size={15}
              strokeWidth={2.5}
              className="mt-[0.15em] flex-shrink-0 text-emerald-500"
            />
            <span>{renderFormattedText(row.custom)}</span>
          </p>
        </div>
      </div>
    </motion.div>
  );
}

export function ComparisonTableSection({ data }: { data?: HealthTechComparison }) {
  const prefersReduced = useReducedMotion();

  if (!data || !data.rows || data.rows.length === 0) return null;

  const [labelCol, offTheShelfCol, customCol] = data.columns;

  return (
    <section className="relative z-10 bg-transparent px-5 py-24 text-foreground sm:px-8 md:py-36 lg:px-12">
      <div className="mx-auto max-w-[88rem]">
        <div className="max-w-3xl">
          {data.eyebrow && (
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-foreground/70">
              {data.eyebrow}
            </p>
          )}
          <h2 className="mt-3 font-display text-balance text-3xl font-semibold leading-[1.08] tracking-[-0.02em] md:text-4xl">
            {data.title} {data.boldTitle}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-foreground/80 md:text-lg">
            {renderFormattedText(data.description)}
          </p>
        </div>

        {/* Mobile: stacked criterion cards, no grid lines to keep alignment simple on narrow screens. */}
        <div className="mt-14 md:hidden">
          {data.rows.map((row, index) => (
            <ComparisonRowMobile key={row.label} row={row} index={index} />
          ))}
        </div>

        {/* Desktop: a genuine gridded table — continuous vertical rules between the
            three columns and a continuous horizontal rule per row, all drawn with
            hairline borders (no gap in the grid so the lines don't break at the gutters). */}
        <motion.div
          role="table"
          aria-label={`${data.title} ${data.boldTitle ?? ""}`}
          className="mt-14 hidden md:grid md:grid-cols-12"
          initial={prefersReduced ? false : { opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: EASE }}
        >
          <div role="row" className="contents">
          <div
            role="columnheader"
            className="col-span-3 border-b border-border pb-4 pr-8 text-xs font-medium uppercase tracking-[0.22em] text-foreground/70"
          >
            {labelCol?.label}
          </div>
          <div
            role="columnheader"
            className="col-span-4 border-b border-l border-border pb-4 pl-8 pr-8 text-xs font-medium uppercase tracking-[0.22em] text-foreground/70"
          >
            {offTheShelfCol?.label}
            {offTheShelfCol?.subtext && (
              <span className="mt-1 block text-xs font-normal normal-case tracking-normal text-foreground/65">
                {offTheShelfCol.subtext}
              </span>
            )}
          </div>
          <div
            role="columnheader"
            className="col-span-5 border-b border-l border-border pb-4 pl-8 text-xs font-medium uppercase tracking-[0.22em] text-foreground/70"
          >
            {customCol?.label}
          </div>
          </div>

          {data.rows.map((row) => (
            <div role="row" className="contents" key={row.label}>
              <div role="rowheader" className="col-span-3 border-t border-border py-6 pr-8">
                <h3 className="font-display text-base font-semibold leading-snug tracking-[-0.01em] text-foreground lg:text-lg">
                  {row.label}
                </h3>
              </div>
              <div role="cell" className="col-span-4 border-t border-l border-border py-6 pl-8 pr-8">
                <p className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground/60 lg:text-base">
                  <X
                    aria-hidden="true"
                    size={16}
                    strokeWidth={2.5}
                    className="mt-[0.15em] flex-shrink-0 text-destructive"
                  />
                  <span>{renderFormattedText(row.offTheShelf)}</span>
                </p>
              </div>
              <div role="cell" className="col-span-5 border-t border-l border-border py-6 pl-8">
                <p className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground lg:text-base">
                  <Check
                    aria-hidden="true"
                    size={16}
                    strokeWidth={2.5}
                    className="mt-[0.15em] flex-shrink-0 text-emerald-500"
                  />
                  <span>{renderFormattedText(row.custom)}</span>
                </p>
              </div>
            </div>
          ))}
        </motion.div>

        {(data.closingLine || data.ctaText) && (
          <div className="mt-14 text-center">
            {data.closingLine && (
              <p className="mx-auto max-w-2xl text-lg leading-relaxed text-foreground/80 md:text-xl">
                {renderFormattedText(data.closingLine)}
              </p>
            )}
            {data.ctaText && data.ctaHref && (
              <div className="mt-8">
                <Button href={data.ctaHref} variant="primary">
                  {data.ctaText}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Product showcase — a screenshot carrying its own caption, for claims that are
// easier to show than to describe. Editorial 5/7 split with a hairline frame
// rather than a card, per the design guide: no shadow, no rounded panel.
// ---------------------------------------------------------------------------
function ProductShowcase({ data }: { data: HealthTechShowcase }) {
  const prefersReduced = useReducedMotion();

  return (
    <section className="relative z-10 bg-transparent px-5 py-24 text-foreground sm:px-8 md:py-36 lg:px-12">
      <div className="mx-auto max-w-[88rem]">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <h2 className="font-display text-balance text-3xl font-semibold leading-[1.08] tracking-[-0.02em] md:text-4xl">
              {data.title} {data.boldTitle}
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-foreground/80 md:text-lg">
              {renderFormattedText(data.description)}
            </p>

            {data.points && data.points.length > 0 && (
              <ul className="mt-8">
                {data.points.map((point) => (
                  <li
                    key={point}
                    className="flex gap-3 border-t border-border py-4 last:border-b"
                  >
                    <span
                      aria-hidden="true"
                      className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-orange-500"
                    />
                    <span className="text-base leading-relaxed text-foreground/80">
                      {renderFormattedText(point)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <motion.figure
            className="lg:col-span-7"
            initial={prefersReduced ? false : { opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <div className="overflow-hidden border border-border">
              <Image
                src={data.image.src}
                alt={data.image.alt}
                width={data.image.width}
                height={data.image.height}
                sizes="(min-width: 1024px) 58vw, 100vw"
                className="h-auto w-full"
              />
            </div>
            {data.caption && (
              <figcaption className="mt-4 text-sm leading-relaxed text-foreground/60">
                {renderFormattedText(data.caption)}
              </figcaption>
            )}
          </motion.figure>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sources — outbound citations to authoritative standards bodies (HL7, ONC,
// CMS, etc.). Strengthens GEO source-authority; only renders when a page
// supplies `topic.sources`, so it's a no-op for every other health-tech page.
// ---------------------------------------------------------------------------
export function SourcesSection({ sources }: { sources?: { label: string; url: string }[] }) {
  if (!sources || sources.length === 0) return null;

  return (
    <section className="relative z-10 bg-transparent px-5 pb-24 text-foreground sm:px-8 lg:px-12">
      <div className="mx-auto max-w-[88rem]">
        <h2 className="font-display text-xl font-semibold tracking-[-0.01em] text-foreground">
          Sources &amp; Standards
        </h2>
        <ul className="mt-4 flex flex-wrap gap-x-8 gap-y-2">
          {sources.map((source) => (
            <li key={source.url}>
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-foreground/70 underline decoration-foreground/30 underline-offset-4 transition-colors hover:text-orange-500 hover:decoration-orange-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
              >
                {source.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Main template export
// ---------------------------------------------------------------------------
export default function HealthTechTemplate({ topic, related }: HealthTechTemplateProps) {
  const faqItems = topic.faq.map((f) => ({
    question: f.question,
    answer: renderFormattedText(f.answer) as React.ReactNode,
  }));

  const processSteps = topic.process.steps.map((step) => ({
    title: step.title,
    description: step.description,
  }));

  return (
    <main className="relative min-h-screen bg-background text-foreground">
      <Hero data={topic.hero} hubIcon={topic.hubIcon} />
      {topic.showcase && <ProductShowcase data={topic.showcase} />}
      {topic.specialties && (
        <CapabilitySection data={topic.specialties} allOpen={topic.specialtiesAllOpen} />
      )}
      <BenefitsRows data={topic.overview} />
      {topic.templates && <BenefitsRows data={topic.templates} />}
      <ComparisonTableSection data={topic.comparison} />
      {topic.journey && <TelemedicineJourney data={topic.journey} />}
      <CapabilitySection data={topic.features} />
      {topic.ecosystem && <IntegrationEcosystemDiagram data={topic.ecosystem} />}
      <BenefitsRows data={topic.whyChoose} />
      <ProcessSection
        title={topic.process.title}
        boldTitle={topic.process.boldTitle}
        steps={processSteps}
      />

      {topic.faq && topic.faq.length > 0 && <FaqSection faqs={faqItems} />}

      <SourcesSection sources={topic.sources} />

      {related}

      <ServiceCtaBand
        headline="Ready to Build Your Healthcare Software"
        subheadline="Let's discuss your project requirements and build something that delivers real clinical and business value."
        ctaText="Start Your Project"
        ctaHref="/inquiry"
      />
    </main>
  );
}
